import { UserProfile } from '@/shared/types';
import { firebaseAuthService } from '@/shared/services/auth/firebaseAuthService';
import { supabaseService } from '@/shared/services/supabaseService';
import { userProfileSyncService } from '@/shared/services/userProfileSyncService';
import { profileService } from '@/shared/services/profiles/profileService';

/**
 * Authentication methods
 * Handles user login, logout, signup, and profile management
 */
export const authService = {
  /**
   * Sign in with email and password
   */
  async login(email: string, password: string): Promise<UserProfile | null> {
    return firebaseAuthService.signIn(email, password);
  },

  /**
   * Sign in with Google OAuth
   */
  async loginWithGoogle(): Promise<UserProfile> {
    return firebaseAuthService.signInWithGoogle();
  },

  /**
   * Sign up a new user
   */
  async signup(
    name: string,
    email: string,
    password: string,
    planId: 'free' | 'basic' | 'pro' = 'free'
  ): Promise<UserProfile> {
    return firebaseAuthService.signUp(email, password, name);
  },

  /**
   * Sign out the current user and clear all session data
   */
  async logout(): Promise<void> {
    try {
      // End session management (clear timers)
      const { sessionService } = await import('@/features/auth/services/sessionService');
      await sessionService.endSession();

      // Sign out from Firebase
      await firebaseAuthService.signOut();

      // Sign out from Supabase
      await supabaseService.signOut();

      // Clear local storage and session storage
      localStorage.clear();
      sessionStorage.clear();

      // Clear any React Query cache if it exists
      // Note: queryClient is not a standard Window property, so we check if it exists
      // @ts-ignore - queryClient is added by React Query devtools/setup
      if (typeof window !== 'undefined' && (window as any).queryClient) {
        (window as any).queryClient.clear();
      }

      // Redirect to login page
      window.location.href = '/auth';

      console.log('User logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, force redirect to login
      window.location.href = '/auth';
    }
  },

  /**
   * Get the current user profile from Firebase auth
   */
  getProfile(): UserProfile | null {
    return firebaseAuthService.getCurrentUser();
  },

  /**
   * Fetch full user profile from Supabase database
   * This gets the complete profile including currency, country, etc.
   */
  async fetchProfile(): Promise<UserProfile | null> {
    const supabaseUserId = await this.getCurrentUserSupabaseId();
    if (!supabaseUserId) return null;

    try {
      const { data, error } = await supabaseService.client
        .from('profiles')
        .select('*')
        .eq('id', supabaseUserId)
        .single();

      if (error) throw error;
      if (!data) return null;

      // Map database profile to UserProfile
      const { mapDatabaseUserToProfile } = await import('@/shared/services/dataMapper');
      return mapDatabaseUserToProfile(data);
    } catch (error) {
      console.error('Error fetching profile from database:', error);
      return null;
    }
  },

  /**
   * Update the current user's profile
   * Updates both Firebase Auth and Supabase database
   */
  async updateProfile(updates: Partial<UserProfile>): Promise<UserProfile> {
    const user = this.getProfile();
    if (!user) throw new Error('User not authenticated');

    // Get the Supabase ID for this user
    const supabaseId = await this.getCurrentUserSupabaseId();
    if (!supabaseId) throw new Error('User Supabase ID not found');

    // Prepare update data for profileService
    const updateData: Record<string, unknown> = {};

    // Map UserProfile fields to ProfileUpdateInput
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.email !== undefined) updateData.email = updates.email;
    if (updates.businessName !== undefined) updateData.business_name = updates.businessName;
    if (updates.businessAddress !== undefined) updateData.business_address = updates.businessAddress;
    if (updates.businessEmail !== undefined) updateData.business_email = updates.businessEmail;
    if (updates.businessPhone !== undefined) updateData.business_phone = updates.businessPhone;
    if (updates.logoUrl !== undefined) updateData.logo_url = updates.logoUrl;
    if (updates.brandColor !== undefined) updateData.brand_color = updates.brandColor;
    if (updates.template !== undefined) updateData.template = updates.template;
    if (updates.currency !== undefined) updateData.currency = updates.currency;
    if (updates.country !== undefined) updateData.country = updates.country;
    if (updates.hasCompletedOnboarding !== undefined) updateData.has_completed_onboarding = updates.hasCompletedOnboarding;
    if (updates.notificationPreferences !== undefined) updateData.notification_preferences = updates.notificationPreferences;
    if (updates.avatarUrl !== undefined) updateData.avatar_url = updates.avatarUrl;

    // Update Supabase profile
    const updatedProfile = await profileService.updateProfile(supabaseId, updateData);

    // Update Firebase Auth profile if name changed
    if (updates.name && updates.name !== user.name) {
      await firebaseAuthService.updateProfile({ name: updates.name });
    }

    return updatedProfile;
  },

  /**
   * Get the current user's Supabase UUID for database queries
   */
  async getCurrentUserSupabaseId(): Promise<string | null> {
    const firebaseUser = firebaseAuthService.getAuth()?.currentUser;
    if (!firebaseUser) return null;

    try {
      return await userProfileSyncService.getCurrentUserSupabaseId(firebaseUser);
    } catch (error) {
      console.error('Error getting user Supabase ID:', error);
      return null;
    }
  }
};
